import {Inject, Injectable, OnDestroy} from '@angular/core';
import {DOCUMENT} from '@angular/common';

/** The ringtone an incoming call plays, on loop, until it is answered, declined or ends. */
export const CallRingtone = 'assets/sounds/meet-notification-sound.mp3';

/** The interactions that count as the user gesture a browser wants before it plays sound. */
const UnlockEvents = ['pointerdown', 'keydown'];

/**
 * What makes an incoming call heard (ITP-E-170-05): the ringtone, and a desktop notification while the tab is
 * hidden. Every open tab rings, so every tab runs one; they share the notification's tag, so the desktop shows
 * one notification however many tabs are open.
 *
 * A browser blocks sound in a tab nobody has touched yet. Then the ringtone starts on the first click or key in
 * the page — the same fallback as the Meet room's blocked-audio banner (meet-sessionv2's onEnableBlockedAudio).
 */
@Injectable({providedIn: 'root'})
export class CallRinger implements OnDestroy {
  private audio: HTMLAudioElement | null = null;
  private notification: Notification | null = null;
  private ringing: {invitationId: string; callerName: string; video: boolean} | null = null;
  private readonly unlock = () => this.play();
  private readonly onVisibilityChange = () => {
    if (this.ringing && this.document.hidden) {
      this.notify();
    }
  };

  constructor(@Inject(DOCUMENT) private document: Document) {
  }

  /**
   * Starts ringing for one call. Ringing again for the same call is a no-op. `video` says it is a video call
   * (ITP-E-182-05), as the desktop notification then says.
   */
  ring(invitationId: string, callerName: string, video = false): void {
    if (this.ringing?.invitationId === invitationId) {
      return;
    }
    this.stop();
    this.ringing = {invitationId, callerName, video};
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.play();
    if (this.document.hidden) {
      this.notify();
    }
  }

  /** Stops the ringtone and closes the notification. */
  stop(): void {
    this.ringing = null;
    this.removeUnlockListeners();
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    if (this.notification) {
      this.notification.close();
      this.notification = null;
    }
  }

  /** Asks once for permission to show desktop notifications. Call it from a click, where every browser allows it. */
  requestNotificationPermission(): void {
    const notifications = this.notifications();
    if (notifications?.permission === 'default') {
      notifications.requestPermission().catch(() => undefined);
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private play(): void {
    if (!this.ringing) {
      return;
    }
    this.removeUnlockListeners();
    this.audio = this.audio || this.createAudio();
    let playing: Promise<void> | undefined;
    try {
      playing = this.audio.play();
    } catch {
      playing = Promise.reject();
    }
    playing?.catch(() => {
      // Blocked until the user touches the page: ring from their first click or key.
      if (this.ringing) {
        UnlockEvents.forEach(event => this.document.addEventListener(event, this.unlock, {capture: true, once: true}));
      }
    });
  }

  private createAudio(): HTMLAudioElement {
    const audio = new Audio(CallRingtone);
    audio.loop = true;
    return audio;
  }

  private notify(): void {
    const notifications = this.notifications();
    if (!this.ringing || this.notification || notifications?.permission !== 'granted') {
      return;
    }
    try {
      this.notification = new notifications(`${this.ringing.callerName || 'Someone'} is calling`, {
        body: this.ringing.video ? 'Incoming video call' : 'Incoming voice call',
        tag: `call-${this.ringing.invitationId}`,
        requireInteraction: true
      });
      this.notification.onclick = () => {
        this.document.defaultView?.focus();
        this.notification?.close();
      };
    } catch (error) {
      // Some browsers only allow notifications from a service worker; the ringtone still rings.
      console.warn('[calls] the incoming-call notification could not be shown', error);
    }
  }

  private notifications(): typeof Notification | undefined {
    return (this.document.defaultView as any)?.Notification;
  }

  private removeUnlockListeners(): void {
    UnlockEvents.forEach(event => this.document.removeEventListener(event, this.unlock, {capture: true}));
  }
}
